import * as React from 'react'
import { Navigate, useNavigate } from 'react-router-dom'

import { Box, Button, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import FormRow from 'modules/core/components/FormRow'
import Loading from 'modules/core/components/Loading'
import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import SelectField from 'modules/core/components/SelectField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { convertDate, dateToString } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import AccessGroup from 'modules/core/store/models/AccessGroup'

import employees from 'modules/employees/store/collections/employees'
import { useEngagementContext } from 'modules/engagement/hooks/useEngagementContext'
import engagementAuthorization from 'modules/engagement/lib/authorization'
import { useEngagementInsightsData } from 'modules/engagement_insights/hooks/useEngagementInsightsData'
import getCurrentClimate from 'modules/engagement_insights/lib/getCurrentClimate'
import { SelectedSurvey } from 'modules/engagement_insights/lib/widget'
import enpsForm from 'modules/enps/store/collections/forms'
import type EnpsForm from 'modules/enps/store/models/Form'
import { FrequencyOptions } from 'modules/surveys/lib/enums'

interface SurveyData {
  [key: string]: string | null
}

const LaunchSurveysModalPage = () => {
  const { navigation } = useNavigation()
  const { employee: currentEmployee } = useCurrentSession()
  const navigate = useNavigate()
  const { moduleAvailabilities, enpsSubscription, surveysSubscription } = useEngagementContext()
  const can = engagementAuthorization({ moduleAvailabilities, currentEmployee })
  const {
    climate,
    climateStatus,
    emptyPage,
    enps,
    enpsInstalled,
    enpsStatus,
    fetchSurveys,
    surveysInstalled,
    surveyToBeLaunched,
  } = useEngagementInsightsData()

  const today = moment().startOf('day')
  const todayFormated = today.format('YYYY-MM-DD')

  const [running, setRunning] = React.useState(false)

  let enpsLaunch = false,
    climateLaunch = false

  switch (surveyToBeLaunched) {
    case SelectedSurvey.Enps:
      enpsLaunch = true
      break
    case SelectedSurvey.Climate:
      climateLaunch = true
      break
    case SelectedSurvey.Both:
      enpsLaunch = true
      climateLaunch = true
      break
  }

  const HELP_CENTER_URL =
    emptyPage || (enpsLaunch && climate)
      ? 'https://help.factorialhr.com/about-the-engagement-insight-dashboard#v1'
      : enps?.isFinished
      ? 'https://help.factorialhr.com/all-about-enps'
      : 'https://help.factorialhr.com/about-climate-survey'

  const description_2 = i18n.t('engagement_insights.launch.description_2', {
    no_employees: employees.active.length,
  })

  const periodicityOptions = [
    {
      value: 'monthly',
      label: i18n.t('enps.form.periodicity.monthly'),
    },
    {
      value: 'bi_monthly',
      label: i18n.t('enps.form.periodicity.bi_monthly'),
    },
    {
      value: 'quarterly',
      label: i18n.t('enps.form.periodicity.quarterly'),
    },
    {
      value: 'quadrimester',
      label: i18n.t('enps.form.periodicity.quadrimester'),
    },
    {
      value: 'bi_annual',
      label: i18n.t('enps.form.periodicity.bi_annual'),
    },
    {
      value: 'yearly',
      label: i18n.t('enps.form.periodicity.yearly'),
    },
  ]

  const form = React.useRef(
    new Form(
      {
        start_on: dateToString(moment().startOf('day'), 'ui'),
        schedule: 'quarterly',
        end_on: null,
      },
      {
        start_on: 'string',
        schedule: 'string',
        end_on: 'date',
      }
    )
  ).current

  const nameTitle = enpsLaunch
    ? enps?.attributes.name || i18n.t('enps.index.title')
    : climateLaunch
    ? climate?.attributes.name || i18n.t('surveys.creation.templates.climate.title')
    : ''

  const startSurveys = emptyPage || (enps?.isFinished && climate?.isFinished)

  const trackAmplitudeEvents = (eventName: string) => {
    track(eventName, {
      enps_status: enpsStatus,
      climate_status: climateStatus,
    })
  }

  const onClose = () => {
    trackAmplitudeEvents(trackingEvents.engagementInsights.cancelEngagementSurveys)
    navigate(navigation.engagement.insights.fullPath())
  }

  const createClimateSurvey = () => {
    return ApiClient().post('/surveys/v2/surveys/create_from_template', {
      name: i18n.t('surveys.creation.templates.climate.title'),
      description: i18n.t('surveys.creation.templates.climate.description'),
      source: 'climate_survey',
      question_group_config: {
        group: null,
        strategy: AccessGroup.Strategies.withRegularAccess,
        groupable_type: null,
        groupable_id: null,
        members: [],
      },
      start_on: todayFormated,
      frequency: 'quarterly',
    })
  }

  const launchEnps = async (data: SurveyData) => {
    let enpsSurvey = enps

    if (!enpsInstalled || !enpsSurvey) {
      await enpsSubscription?.rpc('activate', {})
      await enpsForm.fetch()
      enpsSurvey = enpsForm?.models[0] as EnpsForm
    }

    if (enpsSurvey) {
      return enpsSurvey.rpc('start_form', data)
    }
  }

  const launchClimateSurvey = async (data: SurveyData) => {
    let climateSurvey = climate

    if (!surveysInstalled) {
      await surveysSubscription?.rpc('activate', {})
    }

    if (!climate) {
      await createClimateSurvey()
      climateSurvey = await getCurrentClimate()
    }

    if (climateSurvey) {
      if (climateSurvey.attributes.answered_questions_count == 0) {
        await climateSurvey?.rpc('schedule', data)
        return climateSurvey?.startSurvey()
      } else {
        if (data.start_on === todayFormated) {
          return climateSurvey?.startSurvey()
        } else {
          await climateSurvey?.rpc('schedule', data)
        }
      }
    }
  }

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const formData = form.data()
    const startDate = convertDate(form.get('start_on').value, 'ui', 'backend')

    const frequency = form.get('schedule').value
    const frequencyVal =
      frequency === 'bi_annual'
        ? FrequencyOptions.semiannual
        : frequency === 'yearly'
        ? FrequencyOptions.annual
        : frequency

    const climateData = {
      duration_in_days: null,
      end_on: null,
      frequency: frequencyVal,
      scheduled_time: null,
      start_on: startDate,
    }

    const enpsData = {
      ...formData,
      start_on: startDate,
    }

    trackAmplitudeEvents(trackingEvents.engagementInsights.submitEngagementSurveys)

    setRunning(true)

    const { success } = await asyncAction(
      async () => {
        if (startSurveys) {
          return Promise.all([launchEnps(enpsData), launchClimateSurvey(climateData)])
        } else {
          if (enpsLaunch) {
            return launchEnps(enpsData)
          }
          if (climateLaunch) {
            return launchClimateSurvey(climateData)
          }
        }
      },
      {
        successMessage: i18n.t(`engagement_insights.launch.surveys.success`),
        errorFlash: true,
      }
    )

    setRunning(false)

    if (success) {
      if (!enpsInstalled || !surveysInstalled) {
        window.location.reload()
      }
      closePortal()
      fetchSurveys && (await fetchSurveys())
    }
  }

  if (!can.launchSurveys || !surveyToBeLaunched) {
    return <Navigate to={navigation.engagement.insights.fullPath()}></Navigate>
  }

  const goToHC = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()
    trackAmplitudeEvents(trackingEvents.engagementInsights.goToHC)
    window.open(HELP_CENTER_URL, '_blank')
  }

  return (
    <Modal
      title={
        startSurveys
          ? i18n.t('engagement_insights.launch.title')
          : `${i18n.t('engagement_insights.launch.launch')} ${nameTitle}`
      }
      illustration="surveys"
      onClose={onClose}
      onSubmit={onSubmit}
      footer={
        <Button disabled={running} submit>
          {i18n.t('engagement_insights.launch.launch')}
        </Button>
      }
    >
      {running ? (
        <Loading />
      ) : (
        <>
          <Box style={{ marginTop: '-16px' }}>
            <Text.Normal size="300" overflowWrap>
              {i18n.t('engagement_insights.launch.description_1')}
              &nbsp;
              <a
                style={{ outline: 0 }}
                href={HELP_CENTER_URL}
                onClick={(event: React.MouseEvent<HTMLAnchorElement>) => goToHC(event)}
              >
                {startSurveys ? i18n.t('engagement_insights.launch.engagement_surveys') : nameTitle}
              </a>
              &nbsp;{description_2}
            </Text.Normal>
            <Box paddingTop="s16">
              <Text.Normal size="300" overflowWrap>
                {startSurveys
                  ? i18n.t('engagement_insights.launch.anonymous_surveys')
                  : i18n.t('engagement_insights.launch.anonymous_survey')}
              </Text.Normal>
            </Box>
          </Box>
          <Box gap="s8" paddingTop="s24">
            <FormRow>
              <SelectField
                label={i18n.t('engagement_insights.launch.frequency')}
                options={periodicityOptions}
                onChange={(value) => form.setValues({ schedule: value })}
                readonly={false}
                value={form.get('schedule').value}
              />
            </FormRow>
            <FormRow>
              <DayPicker
                label={i18n.t('engagement_insights.launch.start_date')}
                mandatory
                minDate={today}
                onChange={(date) => form.setValues({ start_on: date })}
                readonly={false}
                value={form.get('start_on').value}
              />
            </FormRow>
          </Box>
        </>
      )}
    </Modal>
  )
}

export default observer(LaunchSurveysModalPage)
